import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { parseChoiceTags, parseExternalContent } from '../utils'
import { normalizeToolName, humanizeToolName } from '../../../shared/utils/tools'

interface Props {
  content: string
  toolName: string | null
}

/**
 * Renders accumulated streaming text with a blinking gold cursor at the end.
 * Optionally shows a tool activity label below the text.
 */
export function StreamingBubble({ content, toolName }: Props) {
  const { t } = useTranslation('chat')

  const { cleanContent: choiceCleaned } = parseChoiceTags(content)
  const { cleanContent: displayContent } = parseExternalContent(choiceCleaned)

  return (
    <div className="px-4 py-3">
      <div className="text-sm leading-relaxed prose-chat">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
        <motion.span
          className="inline-block w-0.5 h-4 bg-gold ml-0.5 align-middle"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
      {toolName && (
        <div className="flex items-center gap-1.5 mt-2">
          <motion.div
            className="w-1.5 h-1.5 rounded-full bg-gold/60"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <span className="text-pearl/60 text-xs">
            {(() => {
              const normalized = normalizeToolName(toolName)
              return t(`tools.activity.${normalized}`, humanizeToolName(normalized))
            })()}
          </span>
        </div>
      )}
    </div>
  )
}
